import React, { useState } from "react"
import { useTranslation } from "next-i18next"
import tw, { styled } from "twin.macro"
import Link from "next/link"
import { ReactComponent as CopyLinkSVG } from "../../assets/share/copyLink.svg"
import { ReactComponent as SMSSVG } from "../../assets/share/sms.svg"
import { ReactComponent as TwitterSVG } from "../../assets/share/twitter.svg"
import { ReactComponent as InstagramSVG } from "../../assets/share/instagram.svg"
import { ReactComponent as YouTubeSVG } from "../../assets/share/youtube.svg"
import { ReactComponent as TikTokSVG } from "../../assets/share/tiktok.svg"
import { ReactComponent as OpenSeaSVG } from "../../assets/share/opensea.svg"
import { ReactComponent as Video1x1SVG } from "../../assets/share/video1:1.svg"
import { ReactComponent as Video9x16SVG } from "../../assets/share/video9:16.svg"
import { ReactComponent as AudioSVG } from "../../assets/share/audio.svg"
import Spinner from "../spinner"
import { ShareToTwitter } from "../../utils/context"
import { Maybe } from "../../graphql/generated/graphql"

const StyledRow = styled("div")(() => [tw`flex space-x-4`])
const StyledHeading = styled("div")(() => [tw`text-md font-bold`])
const StyledButton = styled("button")(() => [tw`flex flex-col items-center text-center text-2xs`])
const StyledLink = styled("a")(() => [tw`hover:no-underline text-base-5`])
interface InteractiveProps {
  disabled: boolean
}
const CopyLinkButton = styled(CopyLinkSVG)(({ disabled }: InteractiveProps) => [disabled && tw`opacity-40`])
const Download1x1 = styled(Video1x1SVG)(({ disabled }: InteractiveProps) => [disabled && tw`opacity-40`])
const Download9x16 = styled(Video9x16SVG)(({ disabled }: InteractiveProps) => [disabled && tw`opacity-40`])
const Audio = styled(AudioSVG)(({ disabled }: InteractiveProps) => [disabled && tw`opacity-40`])
interface ShareProps {
  shareUrl: string
}
interface DownloadProp {
  shareId: string
}
export enum Rendition {
  Share = "_share",
  Audio = "_audio",
}
const CopyLink = ({ shareUrl }: ShareProps) => {
  const { t } = useTranslation("common")
  const [clicked, setClicked] = useState(false)
  return (
    <StyledButton
      onClick={async () => {
        setClicked(true)
        await navigator.clipboard.writeText(shareUrl)
      }}
      disabled={clicked}
    >
      <CopyLinkButton disabled={clicked} />
      {clicked ? t("share.copied_link") : t("share.copy_link")}
    </StyledButton>
  )
}
const SMS = () => {
  const { t } = useTranslation("common")
  return (
    <StyledButton>
      <SMSSVG />
      {t("share.sms")}
    </StyledButton>
  )
}
const Twitter = ({ shareUrl }: ShareProps) => {
  const { t } = useTranslation("common")
  return (
    <Link href={ShareToTwitter(shareUrl)} passHref>
      <StyledLink>
        <StyledButton>
          <TwitterSVG />
          {t("share.twitter")}
        </StyledButton>
      </StyledLink>
    </Link>
  )
}
const Instagram = ({ shareUrl }: ShareProps) => {
  const { t } = useTranslation("common")
  return (
    <Link href={shareUrl} passHref>
      <StyledLink tw="hover:no-underline text-base-5">
        <StyledButton>
          <InstagramSVG />
          {t("share.instagram")}
        </StyledButton>
      </StyledLink>
    </Link>
  )
}
const YouTube = ({ shareUrl }: ShareProps) => {
  const { t } = useTranslation("common")
  return (
    <Link href={shareUrl} passHref>
      <StyledLink>
        <StyledButton>
          <YouTubeSVG />
          {t("share.youtube")}
        </StyledButton>
      </StyledLink>
    </Link>
  )
}
const TikTok = ({ shareUrl }: ShareProps) => {
  const { t } = useTranslation("common")
  return (
    <Link href={shareUrl} passHref>
      <StyledLink>
        <StyledButton>
          <TikTokSVG />
          {t("share.tiktok")}
        </StyledButton>
      </StyledLink>
    </Link>
  )
}
const OpenSea = ({ shareUrl }: ShareProps) => {
  const { t } = useTranslation("common")
  return (
    <Link href={shareUrl} passHref>
      <StyledLink>
        <StyledButton>
          <OpenSeaSVG />
          {t("share.opensea")}
        </StyledButton>
      </StyledLink>
    </Link>
  )
}
const DownloadVideo1x1 = ({ shareId }: DownloadProp) => {
  const { t } = useTranslation("common")
  const [isDownloading, setIsDownloading] = useState(false)

  return (
    <StyledButton
      data-testid={`${shareId}-download`}
      onClick={async () => {
        setIsDownloading(true)
        await downloadFile(shareId)
        setIsDownloading(false)
      }}
    >
      <div tw="relative">
        <Download1x1 disabled={isDownloading} />
        <div tw="absolute top-1/4 hidden" css={[isDownloading && tw`block`]}>
          <Spinner />
        </div>
      </div>
      {t("share.video1x1")}
    </StyledButton>
  )
}
const DownloadVideo9x16 = ({ shareId }: DownloadProp) => {
  const { t } = useTranslation("common")
  const [isDownloading, setIsDownloading] = useState(false)

  return (
    <StyledButton
      data-testid={`${shareId}-download`}
      onClick={async () => {
        setIsDownloading(true)
        await downloadFile(shareId, Rendition.Share)
        setIsDownloading(false)
      }}
    >
      <div tw="relative">
        <Download9x16 disabled={isDownloading} />
        <div tw="absolute top-1/4 hidden" css={[isDownloading && tw`block`]}>
          <Spinner />
        </div>
      </div>
      {t("share.video9x16")}
    </StyledButton>
  )
}
const DownloadAudio = ({ shareId }: DownloadProp) => {
  const { t } = useTranslation("common")
  const [isDownloading, setIsDownloading] = useState(false)

  return (
    <StyledButton
      data-testid={`${shareId}-download`}
      onClick={async () => {
        setIsDownloading(true)
        await downloadFile(shareId, Rendition.Audio)
        setIsDownloading(false)
      }}
    >
      <div tw="relative">
        <Audio disabled={isDownloading} />
        <div tw="absolute top-1/4 hidden" css={[isDownloading && tw`block`]}>
          <Spinner />
        </div>
      </div>
      {t("share.audio")}
    </StyledButton>
  )
}

export interface ShareContentProps {
  shareId?: string
  shareUrl: string
  igShareLink?: Maybe<string> | undefined
  youTubeUrl?: string
  urlTikTok?: string
  urlOpenSea?: string
  urlSMS?: string
  hasRenditions?: boolean
}
export const ShareContent = (props: ShareContentProps) => {
  const { t } = useTranslation("common")
  const { shareUrl, shareId, igShareLink, youTubeUrl, urlTikTok, urlOpenSea, urlSMS, hasRenditions } = props

  return (
    <div tw="w-full px-8 mobile-lg:(px-0) flex flex-col space-y-4">
      <h3 tw="title-3">{t("share.title")}</h3>

      <div tw="space-y-4">
        <StyledHeading>Share</StyledHeading>
        <StyledRow>
          <CopyLink shareUrl={shareUrl} />
          {urlSMS && <SMS />}
          <Twitter shareUrl={shareUrl} />
        </StyledRow>

        {(urlOpenSea || youTubeUrl || igShareLink || urlTikTok) && (
          <>
            <StyledHeading>View on</StyledHeading>
            <StyledRow>
              {igShareLink && <Instagram shareUrl={igShareLink} />}
              {youTubeUrl && <YouTube shareUrl={youTubeUrl} />}
              {urlTikTok && <TikTok shareUrl={urlTikTok} />}
              {urlOpenSea && <OpenSea shareUrl={urlOpenSea} />}
            </StyledRow>
          </>
        )}

        {shareId && (
          <>
            <StyledHeading>Download</StyledHeading>
            <StyledRow>
              <DownloadVideo1x1 shareId={shareId} />
              {hasRenditions && <DownloadVideo9x16 shareId={shareId} />}
              {hasRenditions && <DownloadAudio shareId={shareId} />}
            </StyledRow>
          </>
        )}
      </div>
    </div>
  )
}
export const preloadFile = async (path: string) => {
  const fileUrl = `https://${process.env.NEXT_PUBLIC_AWS_MEDIA_TRANSCODES_BUCKET}.s3.amazonaws.com/${path}`
  return await fetch(fileUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/mp4",
      "Content-Disposition": `attachment; filename=bonsai-${path}`,
    },
  }).then((response) => response.blob())
}

export const downloadFile = async (shareId: string, rendition?: Rendition) => {
  const isAudio = rendition === Rendition.Audio
  const path = `${shareId}${rendition ?? ""}.${isAudio ? "mp3" : "mp4"}`
  const blob = await preloadFile(path)
  const url = window.URL.createObjectURL(new Blob([blob]))
  const link = document.createElement("a")
  link.style.display = "none"
  link.href = url
  link.setAttribute("download", path)
  document.body.appendChild(link)
  link.click()
  if (link.parentNode) {
    link.parentNode.removeChild(link)
  }
}

export const downloadFileUrl = async (fileUrl: string, fileName: string) => {
  const blob = await fetch(fileUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/csv",
      "Content-Disposition": `attachment; filename=bonsai-${fileName}`,
    },
  }).then((response) => response.blob())
  const url = window.URL.createObjectURL(new Blob([blob]))
  const link = document.createElement("a")
  link.style.display = "none"
  link.href = url
  link.setAttribute("download", fileName)
  document.body.appendChild(link)
  link.click()
  if (link.parentNode) {
    link.parentNode.removeChild(link)
  }
}
